// src/watch_protocol.js — the JavaScript side of the phone <-> Wear OS watch
// protocol (version 1). Pure: no React, no DOM, no Capacitor, so node smokes
// can test it (tools/smoke-69-watch.mjs). The Kotlin side is
// android/wear/protocol (WatchProtocol.kt); both are checked against the same
// JSON fixtures in android/wear/protocol/fixtures/.
//
// Phone -> watch: buildWatchState(...) → one JSON object, published as a Wear
// Data Layer DataItem by the LiftWear plugin (src/native.js).
// Watch -> phone: commands, validated by parseWatchCommand(...):
//   { v:1, id, at, type:'log', sessionId, exId, row, w (lb), reps }
//   { v:1, id, at, type:'rest', sessionId, action:'add'|'skip', seconds }
//   { v:1, id, at, type:'finish', sessionId }
//   { v:1, id, at, type:'sync' }
// Weights are pounds everywhere (the storage unit); `units` is display only.

import {
  setsForSlot, workIndexOf, isMainRow, miniNumber, restAfter, stepFor, SET_TYPES,
} from './session_model.js';
import { nextWorkout } from './progression.js';
import { weekStart } from './analytics.js';

export const WATCH_PROTOCOL = 1;
export const WATCH_LIMITS = { maxLb: 2000, maxReps: 36000, maxRow: 199, maxRestDelta: 600, maxAcks: 40 };
const ID_RE = /^[A-Za-z0-9_.:-]{1,64}$/;

const r3 = (x) => Math.round(x * 1000) / 1000;
const numOrNull = (x) => (typeof x === 'number' && Number.isFinite(x) ? r3(x) : null);
const intOrNull = (x) => (typeof x === 'number' && Number.isFinite(x) ? Math.round(x) : null);

// "Set 2 of 4", "Warm-up 1 of 2", "Drop set", "Mini-set 1", "Set 3 of 3 · to failure".
export function watchRowLabel(slot, row) {
  const rd = slot.rows[row] || { type: 'normal' };
  if (rd.type === 'warmup') {
    const warm = slot.rows.map((r, i) => (r.type === 'warmup' ? i : -1)).filter(i => i >= 0);
    return `Warm-up ${warm.indexOf(row) + 1} of ${warm.length}`;
  }
  if (rd.type === 'mini') return `Mini-set ${miniNumber(slot, row)}`;
  if (rd.type === 'drop') return 'Drop set';
  const main = slot.rows.filter(isMainRow).length;
  const base = `Set ${workIndexOf(slot, row) + 1} of ${main}`;
  return rd.type && rd.type !== 'normal' && SET_TYPES[rd.type] ? `${base} · ${SET_TYPES[rd.type].label.toLowerCase()}` : base;
}

/**
 * The live workout as the watch sees it, or null.
 *   session, slots, units, prefs — as in active.jsx
 *   sugg(slot, row) → { w, reps }   the same suggestion the phone pre-fills
 *   perfFor(slot) → [{ w, reps }]   last time's main sets for the slot
 *   kindFor(slot) → { load, track }
 *   focus → { slotId, row } | null  (focusOf)
 */
export function buildWatchWorkout({ session, slots, units = 'lb', prefs = {}, sugg, perfFor, kindFor, focus, title }) {
  if (!session || session.completedAt || session.retro || !Array.isArray(slots)) return null;
  const restOn = prefs.restAutoStart !== false;
  const exercises = slots.map((slot) => {
    const kind = (kindFor && kindFor(slot)) || { load: 'weighted', track: 'reps' };
    const logged = setsForSlot(session, slot);
    const prev = (perfFor && perfFor(slot)) || [];
    const sets = slot.rows.map((rd, row) => {
      const done = logged.find(s => s.setIdx === row) || null;
      const wi = workIndexOf(slot, row);
      const p = wi >= 0 ? prev[wi] || null : null;
      let w = null, reps = null;
      if (done) { w = done.w; reps = done.reps; }
      else if (sugg && kind.track !== 'distance-time') {
        try { const s = sugg(slot, row) || {}; w = s.w; reps = s.reps; } catch (_) { /* no suggestion */ }
      }
      return {
        row,
        kind: rd.type || 'normal',
        label: watchRowLabel(slot, row),
        w: numOrNull(w),
        reps: intOrNull(reps),
        prevW: p ? numOrNull(p.w) : null,
        prevReps: p ? intOrNull(p.reps) : null,
        restAfter: restOn ? Math.max(0, Math.round(restAfter(session, slots, slot, row, prefs) || 0)) : 0,
        done: !!done,
      };
    });
    return {
      id: slot.id,
      name: (slot.variant && slot.variant.name) || slot.name || 'Exercise',
      track: kind.track || 'reps',
      load: kind.load || 'weighted',
      stepLb: r3(stepFor(slot, units) || 5),
      group: slot.supersetId || null,
      sets,
    };
  });
  const fi = focus ? slots.findIndex(s => s.id === focus.slotId) : -1;
  const rest = session.rest && session.rest.endsAt > 0
    ? { endsAt: Math.round(session.rest.endsAt), total: Math.max(1, Math.round(session.rest.total || 0)), label: session.rest.label || '', fired: !!session.rest.fired }
    : null;
  return {
    sessionId: String(session.id),
    title: title || session.name || 'Workout',
    startedAt: Math.round(session.startedAt || 0),
    exercises,
    focus: fi >= 0 ? { ex: fi, row: focus.row } : null,
    rest,
  };
}

/** Keeps a live workout's rest in step with the session when the workout screen is not mounted. */
export function withSessionRest(workout, session) {
  if (!workout || !session || workout.sessionId !== String(session.id)) return workout;
  const r = session.rest;
  return { ...workout, rest: r && r.endsAt > 0 ? { endsAt: Math.round(r.endsAt), total: Math.max(1, Math.round(r.total || 0)), label: r.label || '', fired: !!r.fired } : null };
}

/** Tile data: the next program workout and this week's sessions (Monday first). */
export function buildWatchSummary(state, now = Date.now()) {
  const programs = (state && state.programs) || [];
  const program = programs.find(p => p.id === state.activeProgramId) || null;
  let next = null;
  if (program) {
    const nx = nextWorkout(state, program.id, now);
    if (nx.day) {
      const when = nx.today ? 'Today' : nx.plannedFor ? `Planned for ${nx.plannedFor}` : 'Up next';
      const detail = nx.hasWeeks ? `${nx.weekLabel || ''}${nx.deload && !/deload/i.test(nx.weekLabel || '') ? ' · deload' : ''}` : `Week ${nx.weekIndex + 1}${program.weeks ? ` of ${program.weeks}` : ''}`;
      next = { title: nx.day.name || 'Workout', when, exercises: (nx.day.exercises || []).length, detail: detail.trim() };
    }
  }
  const ws = weekStart(now);
  const bounds = Array.from({ length: 8 }, (_, i) => { const d = new Date(ws); d.setDate(d.getDate() + i); return d.getTime(); });
  const done = ((state && state.sessions) || []).filter(s => s && s.completedAt && s.startedAt >= bounds[0] && s.startedAt < bounds[7]);
  const days = Array.from({ length: 7 }, (_, i) => done.some(s => s.startedAt >= bounds[i] && s.startedAt < bounds[i + 1]));
  const target = program ? (program.days || []).length : ((state && state.profile && state.profile.daysPerWeek) || 3);
  return { next, week: { done: done.length, target: Math.max(0, target | 0), days } };
}

/** The whole phone -> watch message. */
export function buildWatchState({ units = 'lb', workout = null, next = null, week = null, acks = [], now = Date.now() } = {}) {
  return {
    v: WATCH_PROTOCOL,
    sentAt: Math.round(now),
    units: units === 'kg' ? 'kg' : 'lb',
    workout: workout || null,
    next: next || null,
    week: week || null,
    acks: (acks || []).slice(-WATCH_LIMITS.maxAcks),
  };
}

const isInt = (x) => typeof x === 'number' && Number.isInteger(x);
const isStr = (x) => typeof x === 'string' && x.length > 0;

/**
 * Validates a watch command (object or JSON string). Strict, like the Kotlin
 * parser: → { ok: true, cmd } | { ok: false, error, id }.
 */
export function parseWatchCommand(input) {
  let o = input;
  if (typeof input === 'string') { try { o = JSON.parse(input); } catch (_) { return { ok: false, error: 'json', id: null }; } }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return { ok: false, error: 'shape', id: null };
  const id = typeof o.id === 'string' && ID_RE.test(o.id) ? o.id : null;
  const bad = (error) => ({ ok: false, error, id });
  if (o.v !== WATCH_PROTOCOL) return bad('version');
  if (!id) return bad('id');
  if (!isInt(o.at) || o.at < 0) return bad('at');
  const base = { id, at: o.at };
  switch (o.type) {
    case 'log': {
      if (!isStr(o.sessionId) || !isStr(o.exId)) return bad('target');
      if (!isInt(o.row) || o.row < 0 || o.row > WATCH_LIMITS.maxRow) return bad('row');
      if (typeof o.w !== 'number' || !Number.isFinite(o.w) || o.w < 0 || o.w > WATCH_LIMITS.maxLb) return bad('w');
      if (!isInt(o.reps) || o.reps < 1 || o.reps > WATCH_LIMITS.maxReps) return bad('reps');
      return { ok: true, cmd: { ...base, type: 'log', sessionId: o.sessionId, exId: o.exId, row: o.row, w: o.w, reps: o.reps } };
    }
    case 'rest': {
      if (!isStr(o.sessionId)) return bad('target');
      if (o.action === 'skip') return { ok: true, cmd: { ...base, type: 'rest', sessionId: o.sessionId, action: 'skip', seconds: 0 } };
      if (o.action !== 'add') return bad('action');
      if (!isInt(o.seconds) || o.seconds === 0 || Math.abs(o.seconds) > WATCH_LIMITS.maxRestDelta) return bad('seconds');
      return { ok: true, cmd: { ...base, type: 'rest', sessionId: o.sessionId, action: 'add', seconds: o.seconds } };
    }
    case 'finish':
      if (!isStr(o.sessionId)) return bad('target');
      return { ok: true, cmd: { ...base, type: 'finish', sessionId: o.sessionId } };
    case 'sync':
      return { ok: true, cmd: { ...base, type: 'sync' } };
    default:
      return bad('type');
  }
}

/** The rest after a +/- seconds adjustment (same rule as the phone's rest bar). */
export function adjustedRest(rest, seconds, now = Date.now()) {
  if (!rest) return null;
  const endsAt = Math.max(now + 1000, rest.endsAt + seconds * 1000);
  return { ...rest, endsAt, total: Math.max(1, (rest.total || 0) + seconds), adapted: false };
}
